import { type ComponentType, lazy, type LazyExoticComponent } from 'react';

import { logger } from './logger';

/**
 * A one-shot reload flag at module scope.
 *
 * Cannot use `sessionStorage` (ADR-006), and does not need to: the flag only has to survive within
 * one document, and a module-level variable does exactly that. It also resets on the reload, which
 * is the correct behaviour — the point is to reload once, not to remember forever.
 */
let hasReloadedForChunkError = false;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    if (error.name === 'ChunkLoadError') return true;
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
  }
  return false;
}

/**
 * `React.lazy` plus a single full-document reload when a chunk fails to load.
 *
 * The case this handles is a deploy landing mid-session: the HTML in the player's tab references
 * asset hashes that no longer exist, so every route transition fails with a chunk error until the
 * document is reloaded. A player mid-payment cannot be expected to know that, and an error screen
 * offering "try again" would fail identically every time.
 *
 * A reload rather than a remount, because the stale asset manifest lives in the loaded document —
 * remounting re-requests the same missing hash.
 *
 * Bounded to one attempt: if the reload does not fix it the error is real, and it propagates to the
 * boundary rather than becoming a reload loop on a payment page.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  chunkName?: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error) || hasReloadedForChunkError) {
        logger.error('[lazy] chunk load failed, not retrying', {
          chunkName,
          alreadyReloaded: hasReloadedForChunkError,
          error,
        });
        throw error;
      }

      hasReloadedForChunkError = true;
      logger.warn('[lazy] chunk load failed, reloading once', { chunkName });
      window.location.reload();

      // The reload is asynchronous, so this promise must never settle — resolving would render a
      // component tree that is about to be torn down, and rejecting would flash the error boundary
      // for the frame before navigation.
      return await new Promise<{ default: T }>((_resolve) => {
        void _resolve;
      });
    }
  });
}

/** Test seam. Nothing in app code resets this: within a document, one reload is the whole policy. */
export function resetChunkReloadFlagForTests(): void {
  hasReloadedForChunkError = false;
}
